import { inject, Injectable } from "@angular/core";
import { LocationSelectorComponentApi } from "../Api/LocationSelectorComponentApi";
import { LocationSelectorComponentState } from "./LocationSelectorComponentState";
import { LocationOption } from "../Type/LocationSelectorTypes";
import { LocationNode } from "../Dto/LocationData";
import { getBackendErrorMessage } from "../../../Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class LocationSelectorComponentEffects {
  private readonly locationApi = inject(LocationSelectorComponentApi);
  private readonly locationState = inject(LocationSelectorComponentState);

  async loadLocations(): Promise<void> {
    this.locationState.setLoading(true);
    this.locationState.setError(null);

    try {
      const locations = await this.locationApi.loadLocations();
      this.locationState.setLocations(locations);
      const provinces = this.getProvinces(locations);
      this.locationState.setProvinces(provinces);
      this.locationState.setLoaded(true);
    } catch (error: unknown) {
      const errorMessage = getBackendErrorMessage(error);
      console.error("[LocationEffects] Failed to load locations:", errorMessage);
      this.locationState.setError(errorMessage);
    } finally {
      this.locationState.setLoading(false);
    }
  }

  selectProvince(provinceCode: string): void {
    const locations = this.locationState.locations();
    const districts = this.getDistricts(locations, provinceCode);
    this.locationState.setDistricts(districts);
    this.locationState.setSubdistricts([]);
    this.locationState.setVillages([]);
  }

  selectDistrict(districtCode: string): void {
    const locations = this.locationState.locations();
    const subdistricts = this.getSubdistricts(locations, districtCode);
    this.locationState.setSubdistricts(subdistricts);
    this.locationState.setVillages([]);
  }

  selectSubdistrict(subdistrictCode: string): void {
    const locations = this.locationState.locations();
    const villages = this.getVillages(locations, subdistrictCode);
    this.locationState.setVillages(villages);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectVillage(_villageCode: string): void {
    // Villages are the last level, no further action needed
  }

  reset(): void {
    this.locationState.reset();
  }

  private getProvinces(locations: Record<string, LocationNode>): LocationOption[] {
    return Object.keys(locations)
      .filter((code) => !!locations[code].name)
      .map((code) => ({
        value: code,
        label: locations[code].name!,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private getDistricts(locations: Record<string, LocationNode>, provinceCode: string): LocationOption[] {
    const province = locations[provinceCode];
    if (!province) return [];

    const options: LocationOption[] = [];
    for (const [code, node] of Object.entries(province)) {
      if (code === "name" || code === "level" || code === "code") continue;
      if (typeof node === "object" && node !== null && (node as LocationNode).name) {
        options.push({
          value: `${provinceCode}.${code}`,
          label: (node as LocationNode).name!,
        });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }

  private getSubdistricts(locations: Record<string, LocationNode>, districtCode: string): LocationOption[] {
    const parts = districtCode.split(".");
    if (parts.length !== 2) return [];

    const province = locations[parts[0]];
    if (!province) return [];

    const district = province[parts[1]] as LocationNode | undefined;
    if (!district) return [];

    const options: LocationOption[] = [];
    for (const [code, node] of Object.entries(district)) {
      if (code === "name" || code === "level" || code === "code") continue;
      if (typeof node === "object" && node !== null && (node as LocationNode).name) {
        options.push({
          value: `${districtCode}.${code}`,
          label: (node as LocationNode).name!,
        });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }

  private getVillages(locations: Record<string, LocationNode>, subdistrictCode: string): LocationOption[] {
    const parts = subdistrictCode.split(".");
    if (parts.length !== 3) return [];

    const province = locations[parts[0]];
    if (!province) return [];

    const district = province[parts[1]] as LocationNode | undefined;
    if (!district) return [];

    const subdistrict = district[parts[2]] as LocationNode | undefined;
    if (!subdistrict) return [];

    const options: LocationOption[] = [];

    for (const [code, node] of Object.entries(subdistrict)) {
      if (code === "name" || code === "level" || code === "code") continue;
      if (typeof node === "object" && node !== null && (node as LocationNode).name) {
        options.push({
          value: `${subdistrictCode}.${code}`,
          label: (node as LocationNode).name!,
        });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }
}
